import type { ExperienceList, ExperienceMatter, ObjectiveStatus } from '@matter_extractor/shared';
import { experienceListSchema, experienceMatterSchema } from '@matter_extractor/shared';
import { ApiError } from './api';

/**
 * The calls of l'onglet Matière (§12.4) - one read of the matter, one read of
 * the library the selector offers.
 *
 * ## Why a file of its own rather than two more functions in `session-api.ts`
 *
 * The argument `lib/links-api.ts` and `lib/session-api.ts` each made before it,
 * and it is a merge argument rather than a taste one: three lots of the Sessions
 * package are open at once, and a function added in the middle of a
 * two-hundred-line module is a diff that resolves wrong in the one place nobody
 * re-reads - a request that silently keeps the other branch's shape.
 *
 * `ApiError` is IMPORTED from `api.ts` and never re-declared, or `instanceof`
 * would answer false on one of two classes with one name. The `request` helper
 * below is the deliberate duplication those two files already justify: `api.ts`
 * exports none, and exporting one would be an edit to the file this separation
 * exists to avoid. The single behaviour it must keep is that `details` falls
 * back to the WHOLE body, because a business refusal answers the contract body
 * with no `details` key at all.
 *
 * Every answer is PARSED before it is returned: a response is an external input
 * even when we wrote the server.
 */

const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

/**
 * The credential travels in a cookie and NEVER in the URL - the line the whole
 * administration holds. `as const satisfies` so `credentials` keeps its literal
 * type, the régime of the two files beside this one.
 */
const ADMIN_INIT = {
  headers: { 'content-type': 'application/json' },
  credentials: 'include',
} as const satisfies RequestInit;

async function request(path: string): Promise<unknown> {
  const response = await fetch(`${API_URL}${path}`, ADMIN_INIT);
  if (!response.ok) {
    let message = 'La requête a échoué.';
    let details: unknown;
    let code: string | null = null;
    try {
      const body = (await response.json()) as {
        error?: string;
        message?: string;
        details?: unknown;
      };
      if (body.message) message = body.message;
      details = body.details ?? body;
      code = body.error ?? null;
    } catch {
      // Non-JSON error body: keep the generic message.
    }
    throw new ApiError(response.status, message, details, code);
  }
  return response.json();
}

/**
 * The two filters of §12.4, as the panel holds them.
 *
 * `| undefined` is spelled out on each rather than left to the optional marker:
 * under `exactOptionalPropertyTypes` an optional property refuses an explicit
 * `undefined`, and « tous les statuts » IS an explicit undefined.
 */
export type MatterQuery = {
  readonly status?: ObjectiveStatus | undefined;
  readonly experienceVersionId?: string | undefined;
  /** The search of the bar (recherche-sessions.md section 7): it restricts the SESSIONS read. */
  readonly q?: string | undefined;
};

/**
 * The query string, built in ONE place.
 *
 * The keys are the SERVER's (`status`, `version`) and not the panel's
 * (`experienceVersionId`): the route declares the short ones, and a second
 * spelling here would be a filter that silently never applies - the trap
 * `session-api.ts` names on the seven filters of §12.1.
 */
function matterQuery(filters: MatterQuery): string {
  const query = new URLSearchParams();
  if (filters.status) query.set('status', filters.status);
  if (filters.experienceVersionId) query.set('version', filters.experienceVersionId);
  if (filters.q) query.set('q', filters.q);
  return query.size === 0 ? '' : `?${query.toString()}`;
}

/**
 * `GET /api/experiences/{id}/matter` - the extracts by objective, across every
 * session of the experience (§12.4).
 *
 * The versions the answer carries are the ones the version control OFFERS, and
 * they are computed by the server before the version filter: a control that
 * narrowed itself to the value it already holds would trap an operator on their
 * first pick.
 */
export function fetchExperienceMatter(
  experienceId: string,
  filters: MatterQuery = {},
): Promise<ExperienceMatter> {
  const path = `/api/experiences/${encodeURIComponent(experienceId)}/matter`;
  return request(`${path}${matterQuery(filters)}`).then((body) =>
    experienceMatterSchema.parse(body),
  );
}

/**
 * The library, for the experience selector of the tab.
 *
 * The SAME route §4 already serves whole, and deliberately not a narrower one:
 * a « experiences for a select » endpoint would be a second shape of one list,
 * which is the reasoning `links-api.ts` wrote down for its own selector.
 */
export function fetchExperiencesForMatter(): Promise<ExperienceList> {
  return request('/api/experiences').then((body) => experienceListSchema.parse(body));
}
